import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setPost, setPosts } from "state";
import { formatRelativeTime } from "state/helpers";
import {
  ChatBubbleOutlineOutlined,
  FavoriteBorderOutlined,
  FavoriteOutlined,
  ShareOutlined,
  MoreHorizOutlined,
  SendOutlined,
} from "@mui/icons-material";
import {
  Box,
  Divider,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import { BACKEND_URL } from 'state/constants';
import api from 'state/axios';
import FlexBetween from "components/FlexBetween";
import Friend from "components/Friend";
import UserImage from "components/UserImage";
import WidgetWrapper from "components/WidgetWrapper";

const PostWidget = ({
  postId,
  postUserId,
  name,
  description,
  location,
  picturePath,
  userPicturePath,
  likes,
  comments,
  createdAt,
}) => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.user._id);
  const currentUserPicturePath = useSelector((state) => state.user.picturePath);
  const isLiked = Boolean(likes[userId]);
  const likeCount = Object.keys(likes).length;

  const { palette } = useTheme();
  const main = palette.neutral.main;
  const medium = palette.neutral.deep;
  const primary = palette.primary.main;

  const [isComments, setIsComments] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const patchLike = async () => {
    const response = await api.patch(`/posts/${postId}/like`, { userId });
    dispatch(setPost({ post: response.data }));
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || isSubmitting) return;
    setIsSubmitting(true);
    const response = await api.post(
      `/posts/${postId}/comments`,
      { userId, text: commentText }
    );

    dispatch(setPost({ post: response.data }));
    setCommentText("");
    setIsSubmitting(false);
  };

  const handleDelete = async () => {
    setAnchorEl(null);
    const response = await api.get(`/posts/${postId}`);
    const posts = response.data;

    if (Array.isArray(posts)) dispatch(setPosts({ posts }));
  };
  

  return (
    <WidgetWrapper sx={{ mt: "1.25rem" }}>
      {/* Header: friend info + timestamp + menu */}
      <FlexBetween alignItems="flex-start">
        <Friend
          friendId={postUserId}
          name={name}
          subtitle={location}
          userPicturePath={userPicturePath}
        />
        <Box display="flex" alignItems="center" gap={0.5}>
          {createdAt && (
            <Typography variant="caption" color={medium} sx={{ whiteSpace: "nowrap" }}>
              {formatRelativeTime(createdAt)}
            </Typography>
          )}
          {postUserId === userId && (
            <>
              <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
                <MoreHorizOutlined fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={() => setAnchorEl(null)}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem onClick={handleDelete} sx={{ color: "error.main", fontSize: "0.875rem" }}>
                  Delete Post
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </FlexBetween>

      {/* Post content */}
      {description && (
        <Typography color={main} sx={{ mt: "0.75rem", lineHeight: 1.55 }}>
          {description}
        </Typography>
      )}
      {picturePath && (
        <img
          width="100%"
          height="auto"
          alt="post"
          style={{ borderRadius: "0.75rem", marginTop: "0.75rem", display: "block" }}
          src={BACKEND_URL + `/uploads/posts/${picturePath}`}
        />
      )}

      {/* Like / Comment / Share bar */}
      <FlexBetween mt="0.5rem" pt="0.25rem">
        <FlexBetween gap="1rem">
          <FlexBetween gap="0.25rem">
            <IconButton size="small" onClick={patchLike}>
              {isLiked ? (
                <FavoriteOutlined sx={{ color: primary, fontSize: "1.25rem" }} />
              ) : (
                <FavoriteBorderOutlined sx={{ fontSize: "1.25rem" }} />
              )}
            </IconButton>
            <Typography variant="body2" color={main}>{likeCount}</Typography>
          </FlexBetween>

          <FlexBetween gap="0.25rem">
            <IconButton size="small" onClick={() => setIsComments(!isComments)}>
              <ChatBubbleOutlineOutlined
                sx={{ fontSize: "1.25rem", color: isComments ? primary : undefined }}
              />
            </IconButton>
            <Typography variant="body2" color={main}>{comments.length}</Typography>
          </FlexBetween>
        </FlexBetween>

        <IconButton size="small">
          <ShareOutlined sx={{ fontSize: "1.25rem" }} />
        </IconButton>
      </FlexBetween>

      {/* Comments section */}
      {isComments && (
        <Box mt="0.75rem">
          <Divider sx={{ mb: "0.75rem" }} />

          {/* Comment input */}
          <FlexBetween gap="0.75rem" alignItems="center">
            <UserImage image={currentUserPicturePath} size="32px" />
            <InputBase
              placeholder="Write a comment..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleAddComment();
                }
              }}
              sx={{
                flex: 1,
                backgroundColor: palette.neutral.light,
                borderRadius: "2rem",
                px: 2,
                py: 0.5,
                fontSize: "0.875rem",
              }}
            />
            <IconButton
              size="small"
              onClick={handleAddComment}
              disabled={isSubmitting || !commentText.trim()}
              sx={{ color: primary }}
            >
              <SendOutlined fontSize="small" />
            </IconButton>
          </FlexBetween>

          {/* Comment list */}
          {comments.map((comment, i) => {
            const key = comment._id ?? `${postId}-comment-${i}`;
            const authorName = comment.firstName
              ? `${comment.firstName} ${comment.lastName}`
              : "User";
            const text = comment.text ?? (typeof comment === "string" ? comment : "");

            return (
              <Box
                key={key}
                display="flex"
                gap="0.75rem"
                mt="0.75rem"
                alignItems="flex-start"
              >
                {comment.userPicturePath !== undefined && (
                  <UserImage image={comment.userPicturePath} size="32px" />
                )}
                <Box
                  sx={{
                    backgroundColor: palette.neutral.light,
                    borderRadius: "0.75rem",
                    px: "0.75rem",
                    py: "0.5rem",
                    flex: 1,
                  }}
                >
                  {comment.firstName && (
                    <Typography variant="body2" fontWeight="600" color={main}>
                      {authorName}
                    </Typography>
                  )}
                  <Typography variant="body2" color={main}>
                    {text}
                  </Typography>
                  {comment.createdAt && (
                    <Typography variant="caption" color={medium} sx={{ mt: 0.25, display: "block" }}>
                      {formatRelativeTime(comment.createdAt)}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </WidgetWrapper>
  );
};

export default PostWidget;
